import { ActionContext } from "vuex";
import contactService from "@/services/contactService";
import {
  ContactListParams,
  ContactListItemDto,
  ContactResponseDto,
  CreateContactDto,
  UpdateContactDto,
  CreateContactAddressDto,
  CreateContactMethodDto,
  CreateContactEmploymentDto,
  CreateContactReferenceDto,
} from "@/models/contact.model";
import { ErrorCode } from "@/Enums/ErrorCodes";
import { extractErrorCode } from "@/utils/errorUtils";

interface State {
  contacts: ContactListItemDto[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  currentContact: ContactResponseDto | null;
}

type Ctx = ActionContext<State, any>;

const state: State = {
  contacts: [],
  total: 0,
  page: 1,
  pageSize: 10,
  totalPages: 0,
  currentContact: null,
};

const mutations = {
  setContacts(
    state: State,
    payload: {
      items: ContactListItemDto[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }
  ) {
    state.contacts = payload.items;
    state.total = payload.total;
    state.page = payload.page;
    state.pageSize = payload.pageSize;
    state.totalPages = payload.totalPages;
  },
  setCurrentContact(state: State, payload: ContactResponseDto | null) {
    state.currentContact = payload;
  },
};

const actions = {
  // ==================== CONTACTS ====================

  async loadContacts({ commit, dispatch }: Ctx, params: ContactListParams = {}) {
    try {
      const response = await contactService.listContacts(params);
      commit("setContacts", response);
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async fetchContact({ commit, dispatch }: Ctx, contactId: string) {
    try {
      const contact = await contactService.getContactById(contactId);
      commit("setCurrentContact", contact);
      return contact;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      commit("setCurrentContact", null);
      throw error;
    }
  },

  async fetchContactByIdentifier({ commit, dispatch }: Ctx, identifier: string) {
    try {
      const contact = await contactService.getContactByIdentifier(identifier);
      commit("setCurrentContact", contact);
      return contact;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      commit("setCurrentContact", null);
      throw error;
    }
  },

  async createContact(
    { dispatch }: Ctx,
    payload: CreateContactDto
  ): Promise<ContactResponseDto> {
    try {
      const response = await contactService.createContact(payload);
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      const errorCode = extractErrorCode(error);
      if (errorCode !== ErrorCode.CONTACT_DUPLICATE_ID_NUMBER) {
        await dispatch("notificator/apiError", error, { root: true });
      }
      throw error;
    }
  },

  async updateContact(
    { dispatch }: Ctx,
    payload: { contactId: string; data: UpdateContactDto }
  ): Promise<ContactResponseDto> {
    try {
      const response = await contactService.updateContact(
        payload.contactId,
        payload.data
      );
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      const errorCode = extractErrorCode(error);
      if (errorCode !== ErrorCode.CONTACT_DUPLICATE_ID_NUMBER) {
        await dispatch("notificator/apiError", error, { root: true });
      }
      throw error;
    }
  },

  async updateContactFull(
    { dispatch }: Ctx,
    payload: { contactId: string; data: CreateContactDto }
  ): Promise<ContactResponseDto> {
    try {
      const response = await contactService.updateContactFull(
        payload.contactId,
        payload.data
      );
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      const errorCode = extractErrorCode(error);
      if (errorCode !== ErrorCode.CONTACT_DUPLICATE_ID_NUMBER) {
        await dispatch("notificator/apiError", error, { root: true });
      }
      throw error;
    }
  },

  async deleteContact({ dispatch }: Ctx, contactId: string) {
    try {
      await contactService.deleteContact(contactId);
      dispatch("notificator/success", "delete-success", { root: true });
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  // ==================== ADDRESSES ====================

  async addAddress(
    { dispatch }: Ctx,
    payload: { contactId: string; data: CreateContactAddressDto }
  ) {
    try {
      const response = await contactService.addAddress(
        payload.contactId,
        payload.data
      );
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async updateAddress(
    { dispatch }: Ctx,
    payload: {
      contactId: string;
      addressId: string;
      data: CreateContactAddressDto;
    }
  ) {
    try {
      const response = await contactService.updateAddress(
        payload.contactId,
        payload.addressId,
        payload.data
      );
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async deleteAddress(
    { dispatch }: Ctx,
    payload: { contactId: string; addressId: string }
  ) {
    try {
      await contactService.deleteAddress(payload.contactId, payload.addressId);
      dispatch("notificator/success", "delete-success", { root: true });
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  // ==================== CONTACT METHODS ====================

  async addContactMethod(
    { dispatch }: Ctx,
    payload: { contactId: string; data: CreateContactMethodDto }
  ) {
    try {
      const response = await contactService.addContactMethod(
        payload.contactId,
        payload.data
      );
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async updateContactMethod(
    { dispatch }: Ctx,
    payload: {
      contactId: string;
      methodId: string;
      data: CreateContactMethodDto;
    }
  ) {
    try {
      const response = await contactService.updateContactMethod(
        payload.contactId,
        payload.methodId,
        payload.data
      );
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async deleteContactMethod(
    { dispatch }: Ctx,
    payload: { contactId: string; methodId: string }
  ) {
    try {
      await contactService.deleteContactMethod(
        payload.contactId,
        payload.methodId
      );
      dispatch("notificator/success", "delete-success", { root: true });
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  // ==================== EMPLOYMENTS ====================

  async addEmployment(
    { dispatch }: Ctx,
    payload: { contactId: string; data: CreateContactEmploymentDto }
  ) {
    try {
      const response = await contactService.addEmployment(
        payload.contactId,
        payload.data
      );
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async updateEmployment(
    { dispatch }: Ctx,
    payload: {
      contactId: string;
      employmentId: string;
      data: CreateContactEmploymentDto;
    }
  ) {
    try {
      const response = await contactService.updateEmployment(
        payload.contactId,
        payload.employmentId,
        payload.data
      );
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async deleteEmployment(
    { dispatch }: Ctx,
    payload: { contactId: string; employmentId: string }
  ) {
    try {
      await contactService.deleteEmployment(
        payload.contactId,
        payload.employmentId
      );
      dispatch("notificator/success", "delete-success", { root: true });
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  // ==================== REFERENCES ====================

  async addReference(
    { dispatch }: Ctx,
    payload: { contactId: string; data: CreateContactReferenceDto }
  ) {
    try {
      const response = await contactService.addReference(
        payload.contactId,
        payload.data
      );
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async updateReference(
    { dispatch }: Ctx,
    payload: {
      contactId: string;
      referenceId: string;
      data: CreateContactReferenceDto;
    }
  ) {
    try {
      const response = await contactService.updateReference(
        payload.contactId,
        payload.referenceId,
        payload.data
      );
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async deleteReference(
    { dispatch }: Ctx,
    payload: { contactId: string; referenceId: string }
  ) {
    try {
      await contactService.deleteReference(
        payload.contactId,
        payload.referenceId
      );
      dispatch("notificator/success", "delete-success", { root: true });
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },
};

const getters = {
  contacts: (state: State) => state.contacts,
  total: (state: State) => state.total,
  page: (state: State) => state.page,
  pageSize: (state: State) => state.pageSize,
  totalPages: (state: State) => state.totalPages,
  currentContact: (state: State) => state.currentContact,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
